import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Switch,
  Tooltip
} from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";
import { useSnackbar } from "notistack";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { DEFAULT_PAGE_SIZE, DEFAULT_ROWS_PER_PAGE } from "api/constants";
import { useAccountMembers } from "api/services/useAccountMembers";
import { AddMemberModal, tooltipRoleContentText } from "components/account/AddMemberModal";
import { TooltipModalButton } from "components/common/TooltipModalButton";
import { GroundlightButton } from "new-components/buttons/GroundlightButton";
import { StyledDataGrid } from "new-components/dataGrid/StyledDataGrid";
import { Typography } from "new-components/typography/Typography";
import { AccountMember } from "types/accountMembers";
import { extractPaginateFrom } from "utils/utilityFunctions";

// Constants
const DEFAULT_DATA_GRID_INITIAL_STATE = {
  pagination: {
    pageSize: DEFAULT_PAGE_SIZE,
  },
};

export function AccountMembers() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [page, setPage] = useState<number>(1);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [paginateFrom, setPaginateFrom] = useState<string | undefined>(undefined);
  const [updatingUserId, setUpdatingUserId] = useState<number | null>(null);
  const [updatingRoleUserId, setUpdatingRoleUserId] = useState<number | null>(null);
  const nextCursorRef = useRef<string | null>(null);
  const prevCursorRef = useRef<string | null>(null);

  const {
    accountMembers,
    isLoadingAccountMembers,
    accountMembersError,
    updateAccountMember,
    refetchAccountMembers,
  } = useAccountMembers({
    getOptions: {
      params: {
        page_size: pageSize,
        paginate_from: paginateFrom,
      },
    },
  });

  const members = accountMembers?.results || [];
  const totalCount = accountMembers?.count || 0;
  const hasNextPage = !!accountMembers?.next;
  const hasPreviousPage = !!accountMembers?.previous;

  useEffect(() => {
    if (accountMembers) {
      nextCursorRef.current = accountMembers.next;
      prevCursorRef.current = accountMembers.previous;
    }
  }, [accountMembers]);

  useEffect(() => {
    if (pageSize !== DEFAULT_PAGE_SIZE) {
      setPage(1);
      setPaginateFrom(undefined);
    }
  }, [pageSize]);

  const handleNextPage = () => {
    if (hasNextPage && nextCursorRef.current) {
      setPage(page + 1);
      setPaginateFrom(extractPaginateFrom(nextCursorRef.current));
    }
  };

  const handlePreviousPage = () => {
    if (hasPreviousPage && prevCursorRef.current) {
      setPage(page - 1);
      setPaginateFrom(extractPaginateFrom(prevCursorRef.current));
    }
  };

  const handleAddMemberSuccess = () => {
    // Refresh the members list after adding a new member
    refetchAccountMembers();
  };

  const handleRoleChange = async (userId: number, newRole: string) => {
    setUpdatingRoleUserId(userId);
    try {
      await updateAccountMember(
        { id: userId, role: newRole },
        {
          onSuccess: () => {
            enqueueSnackbar(`User role updated to ${newRole} successfully`, { variant: "success" });
          },
          onError: (error: any) => {
            enqueueSnackbar(error?.message || "Failed to update user role. Please try again.", { variant: "error" });
          },
        }
      );
    } catch (err) {
      enqueueSnackbar("An error occurred while updating user role", { variant: "error" });
    } finally {
      setUpdatingRoleUserId(null);
    }
  };

  const handleToggleActivation = async (userId: number, currentStatus: boolean) => {
    setUpdatingUserId(userId);
    try {
      await updateAccountMember(
        { id: userId, is_active: !currentStatus },
        {
          onSuccess: () => {
            enqueueSnackbar(`User ${!currentStatus ? "activated" : "deactivated"} successfully`, { variant: "success" });
          },
          onError: (error: any) => {
            enqueueSnackbar(error?.message || "Failed to update user status. Please try again.", { variant: "error" });
          },
        }
      );
    } catch (err) {
      enqueueSnackbar("An error occurred while updating user status", { variant: "error" });
    } finally {
      setUpdatingUserId(null);
    }
  };

  const handleBackClick = () => {
    navigate("/reef/my-account");
  };

  const columnsBaseStyles: Partial<GridColDef> = {
    headerAlign: "center",
    align: "center",
    headerClassName: "text-base",
  };

  // Define columns for the data grid
  const columns: GridColDef[] = [
    { ...columnsBaseStyles, field: "username", headerName: "Username", flex: 1 },
    { ...columnsBaseStyles, field: "email", headerName: "Email", flex: 1.5 },
    {
      ...columnsBaseStyles,
      field: "role",
      headerName: "Role",
      flex: 1,
      renderHeader: () => (
        <Box className="flex justify-between items-center gap-2	truncate whitespace-normal">
          <Typography>Role</Typography>
          <TooltipModalButton
            modalTitle="User Roles"
            modalContent={tooltipRoleContentText}
            color="black"
            size="medium"
            tooltipSize="xs"
          />
        </Box>
      ),
      renderCell: (params: GridRenderCellParams) => {
        const member = params.row as AccountMember;
        return updatingRoleUserId === member.id ? (
          <CircularProgress size={24} />
        ) : (
          <FormControl fullWidth size="small">
            <Select
              data-testid={`list-role-select-${member.id}`}
              value={member.role || "collaborator"}
              onChange={(e) => handleRoleChange(member.id, e.target.value)}
              variant="outlined"
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="collaborator">Collaborator</MenuItem>
              <MenuItem value="labeler">Labeler</MenuItem>
            </Select>
          </FormControl>
        );
      },
    },
    {
      ...columnsBaseStyles,
      field: "is_active",
      headerName: "Active user",
      flex: 1,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const member = params.row as AccountMember;
        return updatingUserId === member.id ? (
          <CircularProgress size={24} />
        ) : (
          <Switch
            data-testid={`toggle-activation-${member.id}`}
            checked={!!member.is_active}
            onChange={() => handleToggleActivation(member.id, !!member.is_active)}
            color="primary"
          />
        );
      },
    },
  ];

  return (
    <Box className="h-full">
      <Box display="flex" justifyContent="space-between" alignItems="center" height="50px">
        <Box display="flex" alignItems="center">
          <Tooltip title="Back to Account">
            <IconButton onClick={handleBackClick} sx={{ mr: 2 }}>
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <GroundlightButton
          variant="filled"
          onClick={() => setIsAddModalOpen(true)}
        >
          <Typography textTransform="uppercase">Add Member</Typography>
        </GroundlightButton>
      </Box>
      <AddMemberModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={handleAddMemberSuccess}
      />

      {accountMembersError && (
        <Box display="flex" justifyContent="center" my={4}>
          <Typography color="error">{accountMembersError.message}</Typography>
        </Box>
      )}

      <Box sx={{ width: "100%" }} className="h-[calc(100%-50px)]">
        <StyledDataGrid
          loading={isLoadingAccountMembers}
          paginationMode="server"
          rows={members}
          rowCount={totalCount}
          columns={columns}
          initialState={DEFAULT_DATA_GRID_INITIAL_STATE}
          getRowId={(row) => row.id}
          page={page - 1}
          onPageChange={(newPage) => {
            if (newPage > page - 1) {
              handleNextPage();
            } else {
              handlePreviousPage();
            }
          }}
          pageSize={pageSize}
          pagination
          onPageSizeChange={(newPageSize: number) => setPageSize(newPageSize)}
          rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE}
        />
      </Box>
    </Box>
  );
}
